// src/components/admin/components/MusicApprovalCard.jsx
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Music, Clock, Play } from 'lucide-react';

export function MusicApprovalCard({
  music,
  approving,
  isCurrentTrack,
  isPlaying,
  onPlay,
  onApprove,
  onReject
}) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    onReject(music.key, rejectReason);
    setShowRejectForm(false);
    setRejectReason('');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`bg-[#243137] border-2 rounded-lg p-6 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] hover:border-[#bd9f67]/40 transition ${
        isCurrentTrack ? 'border-[#bd9f67] shadow-lg' : 'border-[#bd9f67]/20'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="bg-[#bd9f67]/15 p-3 rounded-lg flex-shrink-0">
            <Music size={24} className="text-[#bd9f67]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-[#f9f9f9] truncate">{music.name}</h3>
            <p className="text-sm text-[#a6a6b5]">By {music.artistName}</p>
            {music.album && (
              <p className="text-xs text-[#7d7d8a] mt-1">Album: {music.album}</p>
            )}
            <div className="flex items-center gap-2 mt-2 text-xs text-[#7d7d8a]">
              <Clock size={14} />
              {new Date(music.uploadedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
        </div>
        <span className="bg-yellow-500/15 text-yellow-200 px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ml-2">
          Pending
        </span>
      </div>

      {music.description && (
        <div className="bg-[#1e2a2f] p-3 rounded mb-4 text-sm">
          <p className="text-[#c9c9d4]">{music.description}</p>
        </div>
      )}

      {/* Music Preview Button */}
      <button
        onClick={() => onPlay(music.url, music)}
        className={`w-full flex items-center justify-center gap-2 mb-4 py-3 rounded-lg font-medium transition ${
          isCurrentTrack && isPlaying
            ? 'bg-[#bd9f67] text-[#243137]'
            : 'bg-[#bd9f67]/15 hover:bg-[#bd9f67]/25 text-[#bd9f67]'
        }`}
      >
        <Play size={18} />
        {isCurrentTrack && isPlaying ? 'Playing...' : 'Preview Music'}
      </button>

      {/* Action Buttons */}
      {!showRejectForm ? (
        <div className="flex gap-3">
          <button
            onClick={() => onApprove(music.key)}
            disabled={approving[music.key]}
            className="flex-1 flex items-center justify-center gap-2 bg-green-500/15 hover:bg-green-500/25 text-green-200 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            <Check size={18} />
            Approve
          </button>
          <button
            onClick={() => setShowRejectForm(true)}
            disabled={approving[music.key]}
            className="flex-1 flex items-center justify-center gap-2 bg-red-500/15 hover:bg-red-500/25 text-red-200 py-2 rounded-lg font-medium transition disabled:opacity-50"
          >
            <X size={18} />
            Reject
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
        >
          <label className="block text-sm font-semibold text-red-200">
            Rejection Reason (Optional)
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="E.g., Poor audio quality, copyright concerns, inappropriate content..."
            className="w-full px-3 py-2 bg-transparent text-[#c9c9d4] border border-red-500/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            rows="3"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={approving[music.key]}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium transition disabled:opacity-50"
            >
              Confirm Reject
            </button>
            <button
              onClick={() => {
                setShowRejectForm(false);
                setRejectReason('');
              }}
              className="flex-1 bg-[#bd9f67]/15 hover:bg-[#bd9f67]/25 text-[#bd9f67] py-2 rounded-lg font-medium transition"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}